import { ReactElement, useState } from 'react';
import { toCurrency } from 'utils/numbers';
import Plan from 'models/Plan';
import SalesTaxTooltip from 'components/shared/tooltips/SalesTaxTooltip';
import RetailDeliveryFeeTooltip from 'components/shared/tooltips/RetailDeliveryFeeTooltip';
import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';
import useSubscription from 'hooks/useSubscription';
import { ICoupon } from 'models/Coupon';
import { post } from 'utils/network';
import { ApiRoute } from 'config';
import useAsyncEffect from '../../hooks/useAsyncEffect';

export type orderPricingInfo = {
  shippingAmount: number,
  shippingFree: boolean,
  extendedAreaSurcharge: string,
  totalPrice: number,
  hasGift: boolean,
  hasGiveaway: boolean
  remainingCredit: number,
  crdFee: number,
  tax: number,
  taxAfterDiscount: number,
  surchargeAfterDiscount: string,
  shippingFeeAfterDiscount: number,
  crdFeeAfterDiscount: number,
}

type gonData = {
  coupon?: ICoupon
};

type Props = {
  plan: Plan
  loggedIn: boolean;
};

// This component reuses logic from CheckoutSummary.tsx
function PriceBreakdown({
  plan,
  loggedIn,
}: Props): ReactElement {
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountError, setDiscountError] = useState('');

  // discount code submit is only enabled for logged in users
  let submitDiscountCode = async () => null;
  let appliedDiscountDetails: any = (window.gon as gonData).coupon ?? {};
  let orderPricing: orderPricingInfo;
  let addressesLength = 0;

  if (loggedIn) {
    /* eslint react-hooks/rules-of-hooks: 0 */
    const { currentUser, reload: reloadUser } = useCurrentUser();
    const { addresses } = useAddresses();
    const { subscription, reload: reloadSubscription } = useSubscription();
    const { signup_summary: signupSummary } = subscription;

    addressesLength = addresses.length;
    appliedDiscountDetails = {
      value: signupSummary?.discount?.value || 0,
      couponName: signupSummary?.discount?.coupon_name || '',
      couponValue: signupSummary?.discount?.coupon_value,
      couponType: signupSummary?.discount?.coupon_discount_type,
      couponCount: signupSummary?.discount?.coupon_count,
      couponFreeShipping: currentUser?.subscription?.coupon?.free_shipping,
    };

    let cumulativePrice = signupSummary?.box_total || 0;
    if (appliedDiscountDetails.couponFreeShipping && !signupSummary?.shipping.free) {
      cumulativePrice -= 12;
    }

    orderPricing = {
      shippingAmount: 12,
      shippingFree: signupSummary?.shipping.free,
      extendedAreaSurcharge: signupSummary?.shipping.shipping_surcharge,
      totalPrice: cumulativePrice,
      hasGift: !!appliedDiscountDetails.value
        && signupSummary?.discount?.type === 'gift_discount',
      hasGiveaway: !!appliedDiscountDetails.value
        && signupSummary?.discount?.type === 'giveaway_discount',
      remainingCredit: (currentUser.credit || 0) - appliedDiscountDetails.value > 0
        ? currentUser.credit - appliedDiscountDetails.value
        : 0,
      crdFee: signupSummary?.crd_fee,
      tax: signupSummary?.sales_tax || 0,
      taxAfterDiscount: Number(signupSummary?.sales_tax_after_discount) || 0,
      surchargeAfterDiscount: signupSummary?.shipping.shipping_surcharge_after_discount,
      shippingFeeAfterDiscount: Number(signupSummary?.shipping.shipping_fee_after_discount) || 0,
      crdFeeAfterDiscount: signupSummary?.crd_fee_after_discount,
    };
    // @ts-expect-error TODO: fix null safety
    submitDiscountCode = async () => {
      setDiscountError('');

      const resp = await post(ApiRoute.REDEEM_CODE, {
        code: discountCodeInput,
      });

      setDiscountCodeInput('');

      if (resp.status === 201) {
        reloadUser();
      } else if (resp.status === 422) {
        setDiscountError((resp.parsedBody as Record<string, string>).details);
      }
    };

    useAsyncEffect(async () => {
      reloadSubscription();
    }, [currentUser, addresses]);
  } else {
    appliedDiscountDetails = {
      value: appliedDiscountDetails?.discount_price,
      couponName: appliedDiscountDetails?.discount_code,
      couponValue: appliedDiscountDetails?.discount_price,
      couponType: appliedDiscountDetails?.discount_type,
      couponCount: appliedDiscountDetails?.max_order_count,
      couponFreeShipping: appliedDiscountDetails?.constraints?.free_shipping,
      maxAmount: appliedDiscountDetails?.constraints?.max_amount,
      minimumBasePrice: appliedDiscountDetails?.constraints?.minimum_base_price,
    };

    let discountAmount = (appliedDiscountDetails?.value ?? 0);
    if (appliedDiscountDetails?.couponType === 'percentage_off') {
      discountAmount = (discountAmount / 100) * plan.planPrice;
      if (appliedDiscountDetails.maxAmount) {
        const maxAmount = Number(appliedDiscountDetails.maxAmount);
        discountAmount = Math.min(maxAmount, discountAmount);
      }
    }

    const totalCost = plan.planPrice + ((plan.planPrice >= 100
        || appliedDiscountDetails.couponFreeShipping) ? 0 : 12) - discountAmount;
    orderPricing = {
      shippingAmount: 12,
      shippingFree: plan.planPrice >= 100,
      extendedAreaSurcharge: '0',
      totalPrice: totalCost,
      hasGift: false,
      hasGiveaway: false,
      remainingCredit: 0,
      crdFee: 0,
      tax: 0,
      taxAfterDiscount: 0,
      surchargeAfterDiscount: '0.0',
      shippingFeeAfterDiscount: 12,
      crdFeeAfterDiscount: 0,
    };
  }

  const noGiftAndExtendedAreaSurcharge = !orderPricing.hasGiveaway
    && !orderPricing.hasGift
    && loggedIn;

  function ShippingFee() {
    if (orderPricing.shippingFree || appliedDiscountDetails.couponFreeShipping) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.shippingAmount)}</del>
          &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (orderPricing.shippingAmount !== orderPricing.shippingFeeAfterDiscount) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.shippingAmount)}</del>
          &nbsp;
          <strong>{toCurrency(orderPricing.shippingFeeAfterDiscount)}</strong>
        </span>
      );
    }
    return <span className="float-right">{toCurrency(orderPricing.shippingFeeAfterDiscount)}</span>;
  }

  function ExtendedSurcharge() {
    const displaySurcharge = orderPricing.extendedAreaSurcharge === '0.0'
      ? '--' : toCurrency(orderPricing.extendedAreaSurcharge);
    const displaySurchargeAfterDiscount = orderPricing.surchargeAfterDiscount === '0.0'
      ? '--' : toCurrency(orderPricing.surchargeAfterDiscount);
    if (addressesLength === 0) {
      return <span className="float-right">TBD</span>;
    }
    if (Number(orderPricing.surchargeAfterDiscount) === 0 && displaySurcharge !== '--') {
      return (
        <span className="float-right">
          <del>{displaySurcharge}</del>
          &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (displaySurcharge !== displaySurchargeAfterDiscount) {
      return (
        <span className="float-right">
          <del>{displaySurcharge}</del>
          &nbsp;
          <strong>{displaySurchargeAfterDiscount}</strong>
        </span>
      );
    }
    return <span className="float-right">{displaySurchargeAfterDiscount}</span>;
  }

  function Taxes() {
    if (orderPricing.taxAfterDiscount === 0 && orderPricing.tax > 0) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.tax)}</del>
          &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (orderPricing.tax !== orderPricing.taxAfterDiscount) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.tax)}</del>
          &nbsp;
          <strong>{toCurrency(orderPricing.taxAfterDiscount)}</strong>
        </span>
      );
    }
    return <span className="float-right">{orderPricing.tax === 0 ? '--' : toCurrency(orderPricing.taxAfterDiscount)}</span>;
  }

  function CrdFee() {
    if (orderPricing.crdFeeAfterDiscount === 0) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.crdFee)}</del>
          &nbsp;
          <strong>FREE</strong>
        </span>
      );
    }
    if (orderPricing.crdFee !== orderPricing.crdFeeAfterDiscount) {
      return (
        <span className="float-right">
          <del>{toCurrency(orderPricing.crdFee)}</del>
          &nbsp;
          <strong>{toCurrency(orderPricing.crdFeeAfterDiscount)}</strong>
        </span>
      );
    }
    return <span className="float-right">{toCurrency(orderPricing.crdFeeAfterDiscount)}</span>;
  }

  const couponMessage = ():string => {
    if (appliedDiscountDetails.couponType === 'percentage_off') {
      return `- ${appliedDiscountDetails.couponValue}% off a maximum of ${toCurrency(appliedDiscountDetails.maxAmount)}`;
    }
    return `- ${toCurrency(appliedDiscountDetails.value)} off of your ${toCurrency(appliedDiscountDetails?.minimumBasePrice)} order`;
  };

  return (
    <div className="c-card__content">
      <div className="c-card__prices">
        <p>
          Weekly price
          <span className="float-right">
            {toCurrency(plan.planPrice)}
          </span>
        </p>
        <hr />
        <p>
          First box shipping
          <ShippingFee />
        </p>
        <hr />
        <>
          <p>
            Extended Area Surcharge
            <ExtendedSurcharge />
          </p>
          <hr />
        </>
        <div>
          <p>
            Sales Tax&nbsp;
            <SalesTaxTooltip />
            <Taxes />
          </p>
        </div>

        {!!orderPricing.crdFee && (
        <>
          <hr />
          <div>
            <p>
              Retail Delivery Fee&nbsp;
              <RetailDeliveryFeeTooltip />
              <CrdFee />
            </p>
          </div>
        </>
        )}

        {loggedIn && appliedDiscountDetails.value > 0 && (
        <>
          <hr />
          <p className={appliedDiscountDetails.couponCount > 1 ? 'pb-4' : ''}>
            <span>
              {orderPricing.hasGift || orderPricing.hasGiveaway
                ? 'Your first box discount'
                : `${appliedDiscountDetails.couponName} discount`}
            </span>
            <span className={appliedDiscountDetails.couponCount > 1 ? 'long-line' : 'float-right'}>
              {appliedDiscountDetails.couponType === 'percentage_off'
                ? `-${appliedDiscountDetails.couponValue}%`
                : `-${toCurrency(appliedDiscountDetails.value)}`}
              {appliedDiscountDetails.couponCount > 1 && (
                ` off your first ${appliedDiscountDetails.couponCount} boxes`
              )}
            </span>
          </p>
        </>
        )}

        {!loggedIn && (
        <>
          <hr />
          { appliedDiscountDetails.value ? (
            <>
              <div className="pb-0">
                <span>Discounts:</span>
                <span className="float-right">{`- ${toCurrency(appliedDiscountDetails.couponValue)}`}</span>
              </div>
              <div className="u-text--xs pt-0 c-card__prices__discount-container">
                <span className="c-card__prices__discount-applied">
                  {`${appliedDiscountDetails.couponName.toUpperCase()} Applied`}
                </span>
                {couponMessage()}
              </div>
            </>
          ) : (
            <>
              <p>
                Discount
                <span className="float-right">
                  --
                </span>
              </p>
              <hr />
              <p>
                Add a discount at the next step
              </p>
            </>
          )}
        </>
        )}

        { noGiftAndExtendedAreaSurcharge && (
        <>
          <hr />
          <div
            className="c-signup-checkout__redeem-coupon"
            onClick={() => setShowDiscountInput(!showDiscountInput)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { setShowDiscountInput(!showDiscountInput); }
            }}
            role="button"
            tabIndex={0}
          >
            {`${showDiscountInput ? '-' : '+'} Apply a discount or gift code`}
          </div>

          {showDiscountInput && (
          <div className="row">
            <div className="c-form-group col-12">
              <div
                className={[
                  'c-form-group',
                  discountError ? 'is-invalid' : '',
                ].join(' ')}
              >
                <div className="c-input-group c-input-group--button">
                  <input
                    id="gift_coupon_code"
                    className="smaller-input"
                    type="text"
                    value={discountCodeInput}
                    aria-label="Discount or Gift Code"
                    onChange={(e) => setDiscountCodeInput(e.target.value)}
                  />
                  <button
                    type="button"
                    aria-label="Apply coupon code"
                    className="c-button c-button--primary"
                    disabled={discountCodeInput.trim().length === 0}
                    onClick={submitDiscountCode}
                  >
                    Apply
                  </button>
                </div>
                <div
                  role="alert"
                  aria-live="assertive"
                  id="coupon-code-invalid"
                  className="c-form-group__invalid-text"
                  dangerouslySetInnerHTML={{ __html: discountError }}
                />
              </div>
            </div>
          </div>
          )}
        </>
        )}

        {!!orderPricing.remainingCredit && (
        <>
          <hr />
          <p>
            <span className="u-text--bold">
              Credit:
            </span>
            <br />
            You have&nbsp;
            <span>{toCurrency(orderPricing.remainingCredit)}</span>
            &nbsp;in credit remaining. Credit is automatically
            applied to your next order. Use the form above
            to redeem additional gifts.
          </p>
        </>
        )}

        <hr />

        <h3 className="c-heading c-heading--brand-7">
          Total for your first box:
          <strong className="float-right">
            {toCurrency(orderPricing.totalPrice)}
          </strong>
        </h3>
      </div>
    </div>
  );
}

export default PriceBreakdown;
